import * as R from "ramda";
import xl from "excel4node";
import { format } from "date-fns-tz";
import { getMarketName, getGranName } from "./spotMarket";

type NumberCellType = {
  sheet: any;
  rowData: any;
  rowIndex: number;
  colIndex: number;
  path: string[];
  style?: any;
};

const numberCell = ({
  sheet,
  rowData,
  rowIndex,
  colIndex,
  path,
  style = {},
}: NumberCellType) => {
  const val = R.pathOr(null, path, rowData);
  return R.isNil(val)
    ? sheet
    : sheet
        .cell(rowIndex, colIndex)
        .number(val)
        .style(style);
};
const expandHourlyToQuarterly = (rows: any[], tideTs: number) =>
  R.pipe<any[], any[], any[]>(
    R.chain((row: any) => {
      const dt = new Date(row.dateTimeOffset);
      const ts = dt.getTime();
      if (ts >= tideTs || dt.getMinutes() !== 0) {
        return [row]; // unchanged
      }
      // create the 4 quarters duplicating price (markets: same price per quarter)
      return [0, 15, 30, 45].map((m) => {
        const q = new Date(ts);
        q.setMinutes(m, 0, 0);
        return {
          ...row,
          dateTimeOffset: q.toISOString(),
        };
      });
    }),
    R.sortBy((r: any) => new Date(r.dateTimeOffset).getTime())
  )(rows);
const getQuarterlyInfo = (rows: any[], tideDate?: Date | string | number) => {
  const tideTs =
    tideDate != null ? new Date(tideDate as any).getTime() : undefined;

  if (!rows || rows.length === 0) return [];

  let normalized = rows;

  if (tideTs != null) {
    const timeStamps = R.map(
      (r: any) => new Date(r.dateTimeOffset).getTime(),
      rows
    ) as number[];
    const minTs = Math.min(...timeStamps);
    const maxTs = Math.max(...timeStamps);
    const shouldExpand = tideTs > minTs && tideTs < maxTs;
    if (shouldExpand) {
      normalized = expandHourlyToQuarterly(rows, tideTs);
    }
  }

  return R.map(
    (row: any) => R.assoc("Date", row.dateTimeOffset, row),
    normalized
  );
};
const getQuarterlyDates = (rows: any[], tideDate?: Date | string | number) => {
  const info = getQuarterlyInfo(rows, tideDate);
  const seen = new Set<string>();
  const dates: string[] = [];
  for (const r of info) {
    const d = r.Date;
    if (!seen.has(d)) {
      seen.add(d);
      dates.push(d);
    }
  }
  dates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  return dates;
};

const formatCellDate = (raw: string, gran: string) => {
  try {
    const d = new Date(raw);
    if (gran === "daily")
      return format(d, "dd-MM-yyyy", { timeZone: "Europe/Rome" });
    if (gran === "hourly")
      return format(d, "dd-MM-yyyy'T'HH:00", { timeZone: "Europe/Rome" });
    if (gran === "quarterly")
      return format(d, "dd-MM-yyyy'T'HH:mm", { timeZone: "Europe/Rome" });
    return raw;
  } catch {
    return raw;
  }
};
const getDailyDates = R.pipe<any, any, any>(
  R.map((row: any) =>
    format(new Date(row.dateTimeOffset), "dd-MM-yyyy", {
      timeZone: "Europe/Rome",
    })
  ),
  R.uniq
);
const getHourlyDates = (rows: any[]) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const hourMap = new Map<number, string>();
  for (const row of rows) {
    const d = new Date(row.dateTimeOffset);
    const hourStart = new Date(d);
    hourStart.setMinutes(0, 0, 0);
    const ts = hourStart.getTime();
    if (!hourMap.has(ts)) {
      // Usiamo ISO (coerente con getHourlyInfo che mette Date = hourStart.toISOString())
      hourMap.set(ts, hourStart.toISOString());
    }
  }
  return Array.from(hourMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, iso]) => iso);
};

const getDailyInfo = R.pipe<any, any, any, any, any>(
  R.map((row: any) =>
    R.assoc("Date", format(new Date(row.dateTimeOffset), "dd-MM-yyyy"), row)
  ),
  R.groupBy((x: any) => `${x.Date}-${x.tipoCurvaPrezzi}`),
  R.map((x: any) => {
    const val = x.reduce(
      (a: any, b: any) => ({ ...b, price: a.price + b.price }),
      {
        price: 0,
      }
    );

    return {
      ...val,
      price: val.price / x.length,
    };
  }),
  R.values
);
const getHourlyInfo = (rows: any[]) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const groups: Record<string, any[]> = {};
  for (const row of rows) {
    const d = new Date(row.dateTimeOffset);
    const hourStart = new Date(d);
    hourStart.setMinutes(0, 0, 0);
    const key = `${hourStart.getTime()}__${row.tipoCurvaPrezzi}`;
    (groups[key] = groups[key] || []).push(row);
  }

  const out: any[] = [];
  for (const [key, group] of Object.entries(groups)) {
    const [hourTsStr, curve] = key.split("__");
    const hourTs = Number(hourTsStr);
    const hourStart = new Date(hourTs);
    const hourISO = hourStart.toISOString();

    if (
      group.length === 1 &&
      new Date(group[0].dateTimeOffset).getMinutes() === 0
    ) {
      // Pure hourly singolo
      out.push({
        ...group[0],
        tipoCurvaPrezzi: curve,
        dateTimeOffset: hourISO,
        Date: hourISO,
        // price invariato
      });
      continue;
    }

    // Gruppo con quarti (completi o parziali): media dei price disponibili
    const prices = group
      .map((r) => (typeof r.price === "number" ? r.price : null))
      .filter((p) => p != null) as number[];
    const avg = prices.length
      ? prices.reduce((s, v) => s + v, 0) / prices.length
      : null;

    out.push({
      ...group[0],
      tipoCurvaPrezzi: curve,
      dateTimeOffset: hourISO,
      Date: hourISO,
      price: avg,
    });
  }

  out.sort(
    (a, b) =>
      new Date(a.Date).getTime() - new Date(b.Date).getTime() ||
      String(a.tipoCurvaPrezzi).localeCompare(String(b.tipoCurvaPrezzi))
  );
  return out;
};

const style = {
  alignment: {
    horizontal: ["center"],
  },
  font: {
    bold: true,
  },
  fill: {
    type: "pattern",
    patternType: "solid",
    bgColor: "#99CC00",
    fgColor: "#99CC00",
  },
};
const headers = ({ zones, sheet }: { zones: any; sheet: any }) => {
  sheet
    .cell(2, 1)
    .string("Date")
    .style(style);
  return zones.map((title: string, i: number) =>
    sheet
      .cell(2, R.add(i, 2))
      .string(getMarketName(title))
      .style(style)
  );
};

const headerSatistics = ({ sheet }: { sheet: any }) => {
  sheet
    .cell(1, 1)
    .string("Mercato")
    .style(style);
  sheet
    .cell(1, 2)
    .string("Min")
    .style(style);
  sheet
    .cell(1, 3)
    .string("Avg")
    .style(style);
  sheet
    .cell(1, 4)
    .string("Max")
    .style(style);
  return sheet;
};

const bodySatistics = ({ sheet, data }: { sheet: any; data: any }) => {
  const tableData = R.pipe<any, any, any, any>(
    R.groupBy(R.prop<any, any>("tipoCurvaPrezzi")),
    R.toPairs,
    R.map(([market, val]) => {
      const prices = R.map(R.prop("price"), val) as any;
      return {
        market,
        min: Math.min(...prices),
        max: Math.max(...prices),
        avg: prices.reduce((a: any, b: any) => a + b, 0) / prices.length,
      };
    })
  )(data);

  tableData.map((rowData: any, ri: number) => {
    const info = { sheet, rowData, rowIndex: R.add(ri, 2) };

    sheet
      .cell(R.add(ri, 2), 1)
      .string(getMarketName(R.pathOr("", ["market"], rowData)))
      .style({});
    numberCell({
      ...info,
      colIndex: 2,
      path: ["min"],
    });
    numberCell({
      ...info,
      colIndex: 3,
      path: ["avg"],
    });
    numberCell({
      ...info,
      colIndex: 4,
      path: ["max"],
    });

    return true;
  });
  return sheet;
};

type BodyType = {
  zones: any;
  sheet: any;
  data: any;
  gran: any;
  tide?: any;
};

const body = ({ zones, sheet, data, gran, tide }: BodyType) => {
  const allDates = R.equals(gran, "daily")
    ? getDailyDates(data)
    : R.equals(gran, "hourly")
    ? getHourlyDates(data)
    : getQuarterlyDates(data, tide);

  const uniformData = R.equals(gran, "daily")
    ? getDailyInfo(data)
    : R.equals(gran, "hourly")
    ? getHourlyInfo(data)
    : getQuarterlyInfo(data, tide);

  return R.pipe(
    R.map((rd) => R.filter(R.propEq("Date", rd), uniformData)),
    R.toPairs,
    (x) =>
      x.map(([row, arr]: any, ri: number) => {
        const rawDate = allDates[row];
        sheet
          .cell(R.add(ri, 3), 1)
          .string(formatCellDate(rawDate, gran))
          .style({});
        return zones.map((col: any, ci: number) => {
          const d = R.pipe<any, any, any>(
            R.filter(
              (x: any) =>
                R.equals(x.tipoCurvaPrezzi, col) && R.equals(x.Date, rawDate)
            ),
            R.pathOr(null, [0, "price"])
          )(arr);
          return R.isNil(d)
            ? sheet
            : sheet.cell(R.add(ri, 3), R.add(ci, 2)).number(d);
        });
      })
  )(allDates);
};

type CreateExcelType = { fileName: string; info: any; tide?: any };

export const createExcel = ({ fileName, info, tide }: CreateExcelType) => {
  var wb = new xl.Workbook();
  var ws = wb.addWorksheet("Timeseries");

  ws.row(1).freeze();
  ws.row(2).freeze();
  ws.cell(1, 1)
    .string("Granularity:")
    .style(style);
  ws.cell(1, 2)
    .string(getGranName(info.granularity))
    .style(style);

  headers({ zones: info.selectedMarketZones, sheet: ws });
  body({
    zones: info.selectedMarketZones,
    sheet: ws,
    data: info.marketsData,
    gran: info.granularity,
    tide: tide,
  });

  var wsSatistics = wb.addWorksheet("Statistics");
  headerSatistics({ sheet: wsSatistics });
  bodySatistics({ sheet: wsSatistics, data: info.marketsData });

  return wb.writeToBuffer().then(function(buffer: any) {
    var a = window.document.createElement("a");
    a.href = window.URL.createObjectURL(
      new Blob([buffer], { type: "application/octet-stream" })
    );
    a.download = `${fileName}.xlsx`;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
  });
};
